/**
 * 统一群组（Group）服务 — 对接 /api/aie/group/*
 * Phase D：取代 family service 的群组部分，支持 family/class/community 通用群组。
 */
export type GroupType = 'family' | 'class' | 'community'
export type Permission = 'owner' | 'admin' | 'member'
/** 邀请制铁律（2026-10-15 F1）：一律邀请制，不提供「开放加入」选项 */
export type JoinPolicy = 'invite_code' | 'admin_invite' | 'closed'

export const JOIN_POLICY_LABEL: Record<JoinPolicy, string> = {
  invite_code: '凭邀请码',
  admin_invite: '仅管理员邀请',
  closed: '禁止加入',
}

export interface MyGroupInfo {
  groupId: string
  name: string
  groupType: GroupType
  description?: string
  joinPolicy: JoinPolicy
  muteAll: boolean
  myPermission: Permission
  myDisplayRole: string
  myMuted: boolean
  status: 'active' | 'archived'
  archivedAt?: string | null
  ownerUserId?: string
  createdAt: string
}

/** 我所在群里已生效共享的小伙伴卡片（GET /aie/group/shared-buddies） */
export interface GroupSharedBuddy {
  scenarioType: string
  displayName: string
  icon: string
  color: string
  description: string
  authorName: string
  /** 有独立 agent 才有（对话入口用）；轻定制共享为空串 */
  agentId: string
  /** 来自我所在的哪些群 */
  groupNames: string[]
  liked: boolean
  likeCount: number
}

export interface ClassExt {
  subject?: string
  semester?: string
  schedule?: string
  schoolName?: string
}

export interface CreateGroupRequest {
  name: string
  groupType: GroupType
  description?: string
  displayRole?: string
  classExt?: ClassExt
  joinPolicy?: JoinPolicy
}

export interface UpdateGroupSettings {
  name?: string
  description?: string
  joinPolicy?: JoinPolicy
  muteAll?: boolean
}

interface ApiClient {
  get<T>(url: string): Promise<T>
  post<T>(url: string, data?: unknown): Promise<T>
  put<T>(url: string, data?: unknown): Promise<T>
  delete<T>(url: string, data?: unknown): Promise<T>
}

class GroupService {
  private apiClient: ApiClient | null = null

  private async getClient(): Promise<ApiClient> {
    if (!this.apiClient) {
      const { apiClient } = await import('./api')
      this.apiClient = apiClient as ApiClient
    }
    return this.apiClient
  }

  async getMyGroups(groupType?: GroupType): Promise<MyGroupInfo[]> {
    const c = await this.getClient()
    const url = groupType ? `/aie/group/mine?groupType=${groupType}` : '/aie/group/mine'
    const data = await c.get<{ data: MyGroupInfo[] }>(url)
    return data.data || []
  }

  async createGroup(req: CreateGroupRequest): Promise<string> {
    const c = await this.getClient()
    const data = await c.post<{ data: { groupId: string } }>('/aie/group/create', req)
    return data.data.groupId
  }

  async validateCode(code: string): Promise<{ groupId: string; groupName: string; groupType: GroupType; displayRole: string } | null> {
    const c = await this.getClient()
    try {
      const data = await c.post<{ data: { groupId: string; groupName: string; groupType: GroupType; displayRole: string } }>(
        `/aie/group/validate-invite/${code}`,
      )
      return data.data
    } catch {
      return null
    }
  }

  async joinByCode(code: string, displayRole?: string): Promise<void> {
    const c = await this.getClient()
    await c.post('/aie/group/join', { code, displayRole })
  }

  // ─── D2：详情 / 成员管理 ───────────────────────────────
  async getDetail(id: string): Promise<GroupDetail> {
    const c = await this.getClient()
    const data = await c.get<{ data: GroupDetail }>(`/aie/group/${id}`)
    return data.data
  }

  async getMembers(id: string): Promise<GroupMemberInfo[]> {
    const c = await this.getClient()
    const data = await c.get<{ data: GroupMemberInfo[] }>(`/aie/group/${id}/members`)
    return data.data || []
  }

  /** 【分享统一】移除群内分享的小伙伴（群 owner/admin 或分享者本人可调） */
  async removeSharedAgent(id: string, scenarioType: string): Promise<void> {
    const c = await this.getClient()
    await c.delete(`/aie/group/${id}/shared-agents/${encodeURIComponent(scenarioType)}`)
  }

  /** 【群审批】群主同意分享（写 group ACL，成员立即可用） */
  async approveSharedAgent(id: string, scenarioType: string): Promise<void> {
    const c = await this.getClient()
    await c.put(`/aie/group/${id}/shared-agents/${encodeURIComponent(scenarioType)}/approve`, {})
  }

  /** 【群审批】群主拒绝分享 */
  async rejectSharedAgent(id: string, scenarioType: string): Promise<void> {
    const c = await this.getClient()
    await c.put(`/aie/group/${id}/shared-agents/${encodeURIComponent(scenarioType)}/reject`, {})
  }

  /** 【来自群】我所在群里已生效共享的小伙伴聚合列表（「我的小伙伴」分区数据源）。
   *  hidden = 本人移除过的（「已移除」恢复入口展示） */
  async getSharedBuddies(): Promise<{ buddies: GroupSharedBuddy[]; hidden: GroupSharedBuddy[] }> {
    const c = await this.getClient()
    const data = await c.get<{ buddies: GroupSharedBuddy[]; hidden: GroupSharedBuddy[] }>(
      '/aie/group/shared-buddies',
    )
    return { buddies: data.buddies || [], hidden: data.hidden || [] }
  }

  /** 【来自群】个人移除（只影响自己的分区显示，不动共享本身） */
  async hideSharedBuddy(scenarioType: string): Promise<void> {
    const c = await this.getClient()
    await c.post(`/aie/group/shared-buddies/${encodeURIComponent(scenarioType)}/hide`, {})
  }

  /** 【来自群】恢复本人移除过的 */
  async restoreSharedBuddy(scenarioType: string): Promise<void> {
    const c = await this.getClient()
    await c.post(`/aie/group/shared-buddies/${encodeURIComponent(scenarioType)}/restore`, {})
  }

  // ─── D3：学习数据（老师/管理员查看群成员学习活跃度）──────────
  async getLearningData(id: string): Promise<LearningDataItem[]> {
    const c = await this.getClient()
    const data = await c.get<{ data: LearningDataItem[] }>(`/aie/group/${id}/learning-data`)
    return data.data || []
  }

  async generateInviteCode(id: string, displayRole = '', expiresInDays = 7): Promise<{ code: string; expiresAt: string }> {
    const c = await this.getClient()
    const data = await c.post<{ data: { code: string; expiresAt: string } }>(`/aie/group/${id}/invite-code`, {
      displayRole,
      expiresInDays,
    })
    return data.data
  }

  async removeMember(id: string, userId: string): Promise<void> {
    const c = await this.getClient()
    await c.delete(`/aie/group/${id}/members/${userId}`)
  }

  async leaveGroup(id: string): Promise<void> {
    const c = await this.getClient()
    await c.post(`/aie/group/${id}/leave`)
  }

  async transferOwnership(id: string, newOwnerId: string): Promise<void> {
    const c = await this.getClient()
    await c.post(`/aie/group/${id}/transfer`, { newOwnerId })
  }

  // ─── 权限扩展：设置 / 管理员 / 禁言 ────────────────────
  async updateSettings(id: string, settings: UpdateGroupSettings): Promise<void> {
    const c = await this.getClient()
    await c.post(`/aie/group/${id}/settings`, settings)
  }

  async updateMemberPermission(id: string, userId: string, permission: 'admin' | 'member'): Promise<void> {
    const c = await this.getClient()
    await c.post(`/aie/group/${id}/members/${userId}/permission`, { permission })
  }

  async muteMember(id: string, userId: string, opts: { durationMinutes?: number; permanent?: boolean }): Promise<void> {
    const c = await this.getClient()
    await c.post(`/aie/group/${id}/members/${userId}/mute`, opts)
  }

  async unmuteMember(id: string, userId: string): Promise<void> {
    const c = await this.getClient()
    await c.post(`/aie/group/${id}/members/${userId}/unmute`)
  }

  // ─── 生命周期：解散 / 恢复 / 彻底删除 ──────────────────
  async archiveGroup(id: string): Promise<void> {
    const c = await this.getClient()
    await c.post(`/aie/group/${id}/archive`)
  }

  async restoreGroup(id: string): Promise<void> {
    const c = await this.getClient()
    await c.post(`/aie/group/${id}/restore`)
  }

  async deleteGroup(id: string): Promise<void> {
    const c = await this.getClient()
    await c.delete(`/aie/group/${id}`)
  }

  async getMyGroupsIncludeArchived(groupType?: GroupType): Promise<MyGroupInfo[]> {
    const c = await this.getClient()
    const base = groupType ? `/aie/group/mine?groupType=${groupType}&includeArchived=true` : '/aie/group/mine?includeArchived=true'
    const data = await c.get<{ data: MyGroupInfo[] }>(base)
    return data.data || []
  }
}

/** 群共享小伙伴卡片（群详情接口返回；分享者为群 owner/admin 的 user_custom 模板） */
export interface SharedAgentCard {
  scenarioType: string
  displayName: string
  icon: string
  color: string
  description: string
  authorName: string
  agentId: string
}

/** 【群审批】待群主同意的分享卡片（仅群主可见） */
export interface PendingSharedAgentCard {
  scenarioType: string
  displayName: string
  icon: string
  description: string
  authorName: string
  requestedAt: string | null
}

/** 群邀请码（详情接口对 owner/admin 返回；过期的不返回，用尽的带 usedCount 供前端标记） */
export interface GroupInviteCodeInfo {
  code: string
  displayRole: string
  maxUses: number
  usedCount: number
  expiresAt: string
  createdAt: string
}

export interface GroupDetail {
  groupId: string
  name: string
  groupType: GroupType
  description?: string
  ownerUserId: string
  joinPolicy: JoinPolicy
  muteAll: boolean
  status: 'active' | 'archived'
  archivedAt?: string | null
  classExt?: ClassExt
  myPermission: Permission
  myDisplayRole: string
  myMuted: boolean
  /** 【分享统一】群共享小伙伴（active 群才有） */
  sharedAgents?: SharedAgentCard[]
  /** 【群审批】待群主同意的分享（仅群主返回） */
  pendingSharedAgents?: PendingSharedAgentCard[]
  /** 邀请码列表（仅 owner/admin 返回；过期已滤除，群主侧持久可见直到失效） */
  inviteCodes?: GroupInviteCodeInfo[]
}

export interface GroupMemberInfo {
  userId: string
  /** users 表显示名（displayRole 是身份标签非人名，可能为空） */
  name?: string
  avatar?: string
  permission: Permission
  displayRole: string
  joinedAt: string
  mutedUntil: string | null
  isMuted: boolean
}

export interface LearningDataItem {
  userId: string
  /** users 表显示名 */
  name?: string
  avatar?: string
  displayRole: string
  totalConversations: number
  totalMessages: number
  lastActiveAt: string | null
  /** conversations 聚合出的真实最近对话时间（覆盖全客户端） */
  lastConversationAt?: string | null
  /** conversations 聚合出的真实对话数 */
  conversationCount?: number
  frequentlyUsedAgents: { scenarioType: string; count: number; lastUsedAt: string }[]
}

export const groupService = new GroupService()
